import CrossPlatformDialog from '@/dialogs/components/CrossPlatformDialog'
import { AudioEngineRemoteAudio, P2pRemoteAudio, SfuRemoteAudio, useRoomContext } from '../../RoomContext'
import { MusePeer } from '../../services/RoomContextMusePeer'
import RemoteAudioSettingsContent from './RemoteAudioSettingsContent'
import { useEffect, useRef, useState } from 'react'
import { TypedEmitter } from 'tiny-typed-emitter'
import { logger } from '@/services/Logger'
import useRoomContextRef from '@/services/useRoomContextRef'

const log = logger.createChildLogger({ name: 'RemoteAudioSettingsDialogController' })

interface RemoteAudioSettingsEvents {
    open: (data: { peer: MusePeer; media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio }) => void
    close: () => void
}

class RemoteAudioSettingsEvent extends TypedEmitter<RemoteAudioSettingsEvents> {
    open(data: { peer: MusePeer; media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio }) {
        this.emit('open', data)
    }

    close() {
        this.emit('close')
    }
}

export const remoteAudioSettingsEvent = new RemoteAudioSettingsEvent()

export default function RemoteAudioSettingsDialogController() {
    const [roomState] = useRoomContext()
    const { roomStateRef } = useRoomContextRef()

    // what the user has selected
    const [selectedDeviceInfo, setSelectedDeviceInfo] = useState<{
        sessionId: string
        deviceId: string
        streamId: string
    } | null>(null)

    // the dialog that is currently being shown
    const [showDeviceSettingsDialog, setShowDeviceSettingsDialog] = useState<{
        peer: MusePeer
        media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio
    } | null>(null)

    // resolver
    const throttleTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)
    const throttleTimer2Ref = useRef<NodeJS.Timeout | undefined>(undefined)
    useEffect(() => {
        if (!selectedDeviceInfo) {
            log.verbose('resolver', 'no selectedDeviceInfo, closing dialog if open.')
            setShowDeviceSettingsDialog(null)
            return
        }

        const peer = roomState.peers[selectedDeviceInfo.sessionId]
        if (!peer) {
            log.verbose('resolver', 'selectedDevice exists but no matching peer, closing dialog if open.', {
                selectedDeviceInfo,
            })
            setSelectedDeviceInfo(null)
            setShowDeviceSettingsDialog(null)
            return
        }

        const media = peer.remoteAudios.find((a) => a.info.deviceId === selectedDeviceInfo.deviceId)
        if (!media) {
            log.verbose(
                'resolver',
                'selectedDevice exists but no matching media exists by deviceId, closing dialog if open.',
                { selectedDeviceInfo }
            )
            setShowDeviceSettingsDialog(null)

            // Launch job to set selectedDeviceInfo to null if the device is gone for good.
            // Otherwise, a device that has the same deviceId but a different streamId may appear, at which
            // point the selectedDeviceInfo will be needed.
            clearTimeout(throttleTimer2Ref.current)
            throttleTimer2Ref.current = setTimeout(() => {
                const peer = roomStateRef.current.peers[selectedDeviceInfo.sessionId]
                if (peer) {
                    const media = peer.remoteAudios.find((a) => a.info.deviceId === selectedDeviceInfo.deviceId)
                    if (!media) {
                        log.verbose(
                            'resolver',
                            'device seems to be gone for good; setting selectedDeviceInfo to null.',
                            { selectedDeviceInfo }
                        )
                        setSelectedDeviceInfo(null)
                    }
                }
            }, 2000)
            return
        }

        if (media.track.id !== selectedDeviceInfo.streamId) {
            log.verbose(
                'resolver',
                'selectedDevice exists and media matching deviceId exists but its streamId does not match, ' +
                    'close dialog and setSelectedDeviceInfo to the current media.',
                { selectedDeviceInfo, mediaTrackId: media.track.id }
            )
            setShowDeviceSettingsDialog(null)
            clearTimeout(throttleTimerRef.current)
            throttleTimerRef.current = setTimeout(() => {
                setSelectedDeviceInfo({
                    sessionId: peer.sessionId,
                    deviceId: media.info.deviceId,
                    streamId: media.track.id,
                })
            }, 50)
            return
        }

        // if deviceId does not match what the user wants to show (selectedDeviceInfo), then trigger.
        if (
            showDeviceSettingsDialog?.peer.sessionId !== selectedDeviceInfo.sessionId ||
            showDeviceSettingsDialog?.media?.info?.deviceId !== selectedDeviceInfo.deviceId
        ) {
            log.verbose('resolver', 'deviceId not matching, show dialog.', {
                selectedDeviceInfo,
                showDeviceSettingsDialog,
            })
            setShowDeviceSettingsDialog(null)
            clearTimeout(throttleTimerRef.current)
            throttleTimerRef.current = setTimeout(() => {
                setShowDeviceSettingsDialog({ peer, media })
            }, 50)
            return
        }
    }, [selectedDeviceInfo, showDeviceSettingsDialog, roomState.peers])

    useEffect(() => {
        const onOpenHandler = (data: {
            peer: MusePeer
            media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio
        }) => {
            setSelectedDeviceInfo({
                sessionId: data.peer.sessionId,
                deviceId: data.media.info.deviceId,
                streamId: data.media.track.id,
            })
        }

        const onCloseHandler = () => {
            setSelectedDeviceInfo(null)
        }

        remoteAudioSettingsEvent.on('open', onOpenHandler)

        remoteAudioSettingsEvent.on('close', onCloseHandler)

        return () => {
            remoteAudioSettingsEvent.off('open', onOpenHandler)
            remoteAudioSettingsEvent.off('close', onCloseHandler)
        }
    }, [])

    if (!showDeviceSettingsDialog) {
        return null
    }

    return (
        <CrossPlatformDialog
            frameName={`Dialog:RemoteAudioSettings-${showDeviceSettingsDialog.media.info.deviceId}`}
            onDefaultClose={() => {
                setSelectedDeviceInfo(null)
            }}
        >
            <RemoteAudioSettingsContent
                peer={showDeviceSettingsDialog.peer}
                media={showDeviceSettingsDialog.media}
                onClose={() => {
                    setSelectedDeviceInfo(null)
                }}
            />
        </CrossPlatformDialog>
    )
}
